import { Box } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SearchFilters } from "../filterChipDefs";
import { ThemeMode } from "../../../theme/ThemeContext";

function popoverColors(_mode: ThemeMode) {
  return {
    text: "var(--text-primary)",
    textSecondary: "var(--text-secondary)",
    accent: "var(--accent)",
    accentDim: "var(--accent-dim)",
    hover: "var(--bg-hover)",
    inputBg: "var(--bg-surface)",
    inputBorder: "var(--border-mid)",
    muted: "var(--text-muted)",
  };
}

function inputSx(c: ReturnType<typeof popoverColors>, disabled: boolean) {
  return {
    width: "100%",
    px: "10px",
    py: "7px",
    fontSize: "0.8125rem",
    fontFamily: "inherit",
    color: disabled ? c.muted : c.text,
    backgroundColor: c.inputBg,
    border: `1px solid ${c.inputBorder}`,
    borderRadius: "6px",
    outline: "none",
    boxSizing: "border-box" as const,
    opacity: disabled ? 0.5 : 1,
    "&:focus": { borderColor: c.accent },
  };
}

function btnSx(c: ReturnType<typeof popoverColors>, primary = false) {
  return {
    px: "12px",
    py: "5px",
    fontSize: "0.75rem",
    fontWeight: 600,
    fontFamily: "inherit",
    borderRadius: "6px",
    cursor: "pointer",
    border: primary ? "none" : `1px solid ${c.inputBorder}`,
    backgroundColor: primary ? c.accent : "transparent",
    color: primary ? "#fff" : c.text,
    "&:hover": { backgroundColor: primary ? c.accent : c.hover, opacity: primary ? 0.9 : 1 },
  };
}

export interface RetentionPopoverProps {
  readonly filters: SearchFilters;
  readonly onFiltersChange: (f: SearchFilters) => void;
  readonly onClose: () => void;
  readonly mode: ThemeMode;
}

type RetentionTri = "any" | "yes" | "no";

function triFromFilters(f: SearchFilters): RetentionTri {
  if (f.hasRetention === true) return "yes";
  if (f.hasRetention === false) return "no";
  if (f.fromRetention || f.toRetention) return "yes";
  return "any";
}

/**
 * Truncate an ISO-ish date string to the "YYYY-MM-DDTHH:mm" form that datetime-local inputs expect.
 * Tolerant of inputs that are already in that form.
 */
function toDatetimeLocal(iso?: string): string {
  if (!iso) return "";
  // Take up through minute precision ("YYYY-MM-DDTHH:mm")
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/.exec(iso);
  return m ? m[1] : iso;
}

export default function RetentionPopover({
  filters,
  onFiltersChange,
  onClose,
  mode,
}: RetentionPopoverProps) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);

  const [tri, setTri] = useState<RetentionTri>(triFromFilters(filters));
  const [fromDraft, setFromDraft] = useState<string>(toDatetimeLocal(filters.fromRetention));
  const [toDraft, setToDraft] = useState<string>(toDatetimeLocal(filters.toRetention));

  const datesDisabled = tri !== "yes";

  const selectTri = (next: RetentionTri) => {
    setTri(next);
    if (next === "any" || next === "no") {
      setFromDraft("");
      setToDraft("");
    }
  };

  const handleFromChange = (v: string) => {
    setFromDraft(v);
    if (v && tri === "any") setTri("yes");
  };

  const handleToChange = (v: string) => {
    setToDraft(v);
    if (v && tri === "any") setTri("yes");
  };

  const handleApply = () => {
    if (tri === "any") {
      onFiltersChange({
        ...filters,
        hasRetention: undefined,
        fromRetention: undefined,
        toRetention: undefined,
      });
    } else if (tri === "no") {
      onFiltersChange({
        ...filters,
        hasRetention: false,
        fromRetention: undefined,
        toRetention: undefined,
      });
    } else {
      onFiltersChange({
        ...filters,
        hasRetention: true,
        fromRetention: fromDraft ? `${fromDraft}:00.000Z` : undefined,
        toRetention: toDraft ? `${toDraft}:59.999Z` : undefined,
      });
    }
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({
      ...filters,
      hasRetention: undefined,
      fromRetention: undefined,
      toRetention: undefined,
    });
    onClose();
  };

  const triButtonSx = (value: RetentionTri) => ({
    flex: 1,
    px: "10px",
    py: "6px",
    fontSize: "0.75rem",
    fontWeight: tri === value ? 600 : 400,
    textAlign: "center" as const,
    color: tri === value ? c.accent : c.text,
    bgcolor: tri === value ? c.accentDim : "transparent",
    border: `1px solid ${tri === value ? c.accent : c.inputBorder}`,
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": { bgcolor: c.hover },
  });

  const labelSx = {
    fontSize: "0.6875rem",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    color: c.textSecondary,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", p: "14px", minWidth: 280 }}>
      <Box component="label" sx={labelSx}>{t("search:hasRetention")}</Box>
      <Box sx={{ display: "flex", gap: "6px" }}>
        <Box component="button" onClick={() => selectTri("any")} sx={triButtonSx("any")}>
          {t("common:any")}
        </Box>
        <Box component="button" onClick={() => selectTri("yes")} sx={triButtonSx("yes")}>
          {t("common:yes")}
        </Box>
        <Box component="button" onClick={() => selectTri("no")} sx={triButtonSx("no")}>
          {t("common:no")}
        </Box>
      </Box>

      <Box component="label" htmlFor="retention-from" sx={labelSx}>{t("search:retentionFrom")}</Box>
      <Box
        id="retention-from"
        component="input"
        type="datetime-local"
        value={fromDraft}
        disabled={datesDisabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFromChange(e.target.value)}
        sx={inputSx(c, datesDisabled)}
      />
      <Box component="label" htmlFor="retention-to" sx={labelSx}>{t("search:retentionTo")}</Box>
      <Box
        id="retention-to"
        component="input"
        type="datetime-local"
        value={toDraft}
        disabled={datesDisabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToChange(e.target.value)}
        sx={inputSx(c, datesDisabled)}
      />

      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", mt: "4px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
        <Box component="button" onClick={handleApply} sx={btnSx(c, true)}>{t("common:apply")}</Box>
      </Box>
    </Box>
  );
}
